const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DISPLAY_DATE_FORMAT = "d M y";
const DISPLAY_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function isValidIsoDate(value) {
    if (!ISO_DATE_PATTERN.test(value)) {
        return false;
    }

    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;
}

function parseIsoDate(value) {
    if (!isValidIsoDate(value)) {
        return null;
    }

    const [year, month, day] = value.split("-").map(Number);

    return new Date(Date.UTC(year, month - 1, day));
}

function formatDisplayDate(iso) {
    if (!isValidIsoDate(iso)) {
        return "";
    }

    const [year, month, day] = iso.split("-").map(Number);

    return `${String(day).padStart(2, "0")} ${DISPLAY_MONTHS[month - 1]} ${String(year).slice(-2)}`;
}

function parseDisplayDateToIso(display) {
    const trimmed = String(display ?? "").trim();

    if (trimmed === "") {
        return "";
    }

    if (isValidIsoDate(trimmed)) {
        return trimmed;
    }

    if (!window.flatpickr) {
        return "";
    }

    const parsed = window.flatpickr.parseDate(trimmed, DISPLAY_DATE_FORMAT);

    if (!parsed) {
        return "";
    }

    const iso = window.flatpickr.formatDate(parsed, "Y-m-d");

    return isValidIsoDate(iso) ? iso : "";
}

function monthStart(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, months) {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function resolveCalendarFocusDate({ preset, start, end, showMonths }) {
    const startDate = parseIsoDate(start);
    const endDate = parseIsoDate(end);
    const today = new Date();

    if (preset === "all-time" || (!startDate && !endDate)) {
        const anchor = endDate || today;

        return showMonths > 1 ? addMonths(monthStart(anchor), -1) : monthStart(anchor);
    }

    if (!endDate) {
        return startDate || today;
    }

    if (!startDate) {
        return showMonths > 1 ? addMonths(monthStart(endDate), -1) : monthStart(endDate);
    }

    const sameMonth = startDate.getFullYear() === endDate.getFullYear()
        && startDate.getMonth() === endDate.getMonth();

    if (sameMonth || preset === "current-month" || preset === "previous-month") {
        return monthStart(startDate);
    }

    if (showMonths > 1) {
        return addMonths(monthStart(endDate), -1);
    }

    return monthStart(endDate);
}

const buildDateRangePicker = ({
    presetModel = null,
    startModel = null,
    endModel = null,
    presetsModel = null,
    applyAction = "applyDateRange",
    presets = [],
    mode = "livewire",
    initialPreset = "all-time",
    initialStart = "",
    initialEnd = "",
    initialSummary = "",
    submitOnApply = false,
    periodName = "period",
    fromName = "from",
    toName = "to",
    labels = {},
}) => ({
    open: false,
    mode,
    submitOnApply,
    periodName,
    fromName,
    toName,
    presets,
    labels: {
        allTime: labels.allTime || "All time",
        custom: labels.custom || "Custom",
        dates: labels.dates || ":from – :to",
        full: labels.full || ":label: :dates",
    },
    instance: null,
    mediaQuery: null,
    mediaQueryHandler: null,
    appliedPreset: initialPreset || "all-time",
    appliedStart: initialStart || "",
    appliedEnd: initialEnd || "",
    summaryText: initialSummary || "",
    draftPreset: "last-90-days",
    draftStart: "",
    draftEnd: "",
    draftStartDisplay: "",
    draftEndDisplay: "",
    lastValidStart: "",
    lastValidEnd: "",
    draftStartInvalid: false,
    draftEndInvalid: false,
    panelStyle: {
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        "transform-origin": "top center",
    },
    viewportHandler: null,

    init() {
        this.mediaQuery = window.matchMedia("(min-width: 640px)");
        this.mediaQueryHandler = () => this.syncMonthCount();
        this.mediaQuery.addEventListener("change", this.mediaQueryHandler);
        this.refreshSummaryText();
        this.syncFormInputs();
    },

    isFormMode() {
        return this.mode === "form";
    },

    isScoped() {
        return this.getAppliedPreset() !== "all-time";
    },

    buildSummary(preset, start, end) {
        if (preset === "all-time") {
            return this.labels.allTime;
        }

        const presetLabel = this.presets.find((option) => option.value === preset)?.label
            || this.labels.custom;
        const from = formatDisplayDate(start);
        const to = formatDisplayDate(end);

        if (!from || !to) {
            return presetLabel;
        }

        const dates = this.labels.dates
            .replace(":from", from)
            .replace(":to", to);

        return this.labels.full
            .replace(":label", presetLabel)
            .replace(":dates", dates);
    },

    refreshSummaryText() {
        this.summaryText = this.buildSummary(
            this.getAppliedPreset(),
            this.getAppliedStart(),
            this.getAppliedEnd(),
        );
    },

    getAppliedPreset() {
        if (this.isFormMode()) {
            return this.appliedPreset || "all-time";
        }

        return this.$wire.get(presetModel) || "last-90-days";
    },

    getAppliedStart() {
        if (this.isFormMode()) {
            return this.appliedStart || "";
        }

        return this.$wire.get(startModel) || "";
    },

    getAppliedEnd() {
        if (this.isFormMode()) {
            return this.appliedEnd || "";
        }

        return this.$wire.get(endModel) || "";
    },

    ensureFormField(form, name) {
        let input = form.querySelector(`input[type="hidden"][name="${name}"]`);

        if (input instanceof HTMLInputElement) {
            return input;
        }

        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        form.appendChild(input);

        return input;
    },

    syncFormInputs() {
        if (!this.isFormMode()) {
            return;
        }

        const form = this.$el instanceof HTMLElement
            ? this.$el.closest("form")
            : null;

        if (!(form instanceof HTMLFormElement)) {
            if (this.$refs.presetInput) {
                this.$refs.presetInput.value = this.appliedPreset || "all-time";
            }

            if (this.$refs.startInput) {
                this.$refs.startInput.value = this.appliedStart || "";
            }

            if (this.$refs.endInput) {
                this.$refs.endInput.value = this.appliedEnd || "";
            }

            return;
        }

        this.ensureFormField(form, this.periodName).value = this.appliedPreset || "all-time";
        this.ensureFormField(form, this.fromName).value = this.appliedStart || "";
        this.ensureFormField(form, this.toName).value = this.appliedEnd || "";
    },

    submitFormFilters() {
        const form = this.$el instanceof HTMLElement
            ? this.$el.closest("form")
            : null;

        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        this.syncFormInputs();

        const action = form.getAttribute("action") || window.location.pathname;
        const method = (form.getAttribute("method") || "get").toLowerCase();

        if (method !== "get") {
            if (typeof form.requestSubmit === "function") {
                form.requestSubmit();
            } else {
                form.submit();
            }

            return;
        }

        const params = new URLSearchParams();

        for (const [key, value] of new FormData(form).entries()) {
            if (String(value) === "") {
                continue;
            }

            params.append(key, String(value));
        }

        params.set(this.periodName, this.appliedPreset || "all-time");

        if (this.appliedStart) {
            params.set(this.fromName, this.appliedStart);
        } else {
            params.delete(this.fromName);
        }

        if (this.appliedEnd) {
            params.set(this.toName, this.appliedEnd);
        } else {
            params.delete(this.toName);
        }

        const query = params.toString();
        window.location.assign(query ? `${action}?${query}` : action);
    },

    destroy() {
        this.unbindViewportListeners();

        if (this.mediaQuery && this.mediaQueryHandler) {
            this.mediaQuery.removeEventListener("change", this.mediaQueryHandler);
        }

        this.mediaQueryHandler = null;
        this.instance?.destroy();
        this.instance = null;
    },

    ensureCalendar() {
        if (this.instance || !this.$refs.calendar) {
            return;
        }

        this.instance = window.flatpickr(this.$refs.calendar, {
            inline: true,
            mode: "range",
            dateFormat: "Y-m-d",
            defaultDate: [],
            animate: false,
            monthSelectorType: "static",
            showMonths: this.mediaQuery.matches ? 2 : 1,
            onChange: (selectedDates) => {
                if (selectedDates.length === 0) {
                    this.draftStart = "";
                    this.draftEnd = "";
                    this.lastValidStart = "";
                    this.lastValidEnd = "";
                    this.draftStartInvalid = false;
                    this.draftEndInvalid = false;
                    this.syncDraftDisplays();

                    return;
                }

                this.draftPreset = "custom";
                this.draftStart = this.instance.formatDate(selectedDates[0], "Y-m-d");
                this.draftEnd = selectedDates[1]
                    ? this.instance.formatDate(selectedDates[1], "Y-m-d")
                    : "";
                this.rememberValidDraftDates();
                this.draftStartInvalid = false;
                this.draftEndInvalid = false;
                this.syncDraftDisplays();
            },
        });
    },

    syncMonthCount() {
        if (!this.instance) {
            return;
        }

        this.instance.set("showMonths", this.mediaQuery.matches ? 2 : 1);
        this.instance.redraw();
        this.syncCalendar();

        if (this.open) {
            requestAnimationFrame(() => this.positionPanel());
        }
    },

    bindViewportListeners() {
        this.viewportHandler = this.viewportHandler ?? (() => {
            requestAnimationFrame(() => this.positionPanel());
        });

        window.addEventListener("scroll", this.viewportHandler, true);
        window.addEventListener("resize", this.viewportHandler, true);
    },

    unbindViewportListeners() {
        if (!this.viewportHandler) {
            return;
        }

        window.removeEventListener("scroll", this.viewportHandler, true);
        window.removeEventListener("resize", this.viewportHandler, true);
        this.viewportHandler = null;
    },

    resolveAnchorRect(trigger) {
        const anchor = trigger.closest("main")
            ?? trigger.closest(".flex-1.flex-col")
            ?? document.documentElement;

        return anchor.getBoundingClientRect();
    },

    positionPanel() {
        const panel = this.$refs.panel;
        const trigger = this.$refs.trigger;

        if (!panel || !trigger) {
            return;
        }

        const previousDisplay = panel.style.display;
        const previousVisibility = panel.style.visibility;

        if (panel.offsetWidth === 0 || panel.offsetHeight === 0) {
            panel.style.visibility = "hidden";
            panel.style.display = "block";
        }

        const spacing = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const triggerRect = trigger.getBoundingClientRect();
        const anchorRect = this.resolveAnchorRect(trigger);
        const panelWidth = panel.offsetWidth;
        const panelHeight = panel.offsetHeight;

        const minLeft = Math.max(spacing, anchorRect.left + spacing);
        const maxLeft = Math.min(
            anchorRect.right - panelWidth - spacing,
            viewportWidth - panelWidth - spacing,
        );

        let left = anchorRect.left + Math.round((anchorRect.width - panelWidth) / 2);
        left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const fitsBelow = spaceBelow >= panelHeight + spacing;
        const fitsAbove = spaceAbove >= panelHeight + spacing;

        let top;
        let transformOrigin = "top center";

        if (fitsBelow || spaceBelow >= spaceAbove) {
            top = Math.min(triggerRect.bottom + spacing, viewportHeight - panelHeight - spacing);
            top = Math.max(top, spacing);
        } else {
            top = Math.max(triggerRect.top - panelHeight - spacing, spacing);
            transformOrigin = "bottom center";
        }

        this.panelStyle = {
            position: "fixed",
            top: `${Math.round(top)}px`,
            left: `${Math.round(left)}px`,
            "transform-origin": transformOrigin,
        };

        panel.style.visibility = previousVisibility;
        panel.style.display = previousDisplay;
    },

    openPicker() {
        this.syncPresetsFromWire();
        this.restoreDraft();
        this.open = true;

        this.$nextTick(() => {
            this.ensureCalendar();
            this.syncCalendar();
            this.instance?.redraw();

            requestAnimationFrame(() => {
                this.positionPanel();
                requestAnimationFrame(() => this.positionPanel());
                this.bindViewportListeners();
            });
        });
    },

    closePicker() {
        this.open = false;
        this.unbindViewportListeners();
        this.draftStartInvalid = false;
        this.draftEndInvalid = false;
        this.panelStyle = {
            position: "fixed",
            top: "-9999px",
            left: "-9999px",
            "transform-origin": this.panelStyle["transform-origin"] ?? "top center",
        };
    },

    syncPresetsFromWire() {
        if (this.isFormMode() || !presetsModel) {
            return;
        }

        const freshPresets = this.$wire.get(presetsModel);

        if (Array.isArray(freshPresets) && freshPresets.length > 0) {
            this.presets = freshPresets;
        }
    },

    restoreDraft() {
        this.draftPreset = this.getAppliedPreset();
        this.draftStart = this.getAppliedStart();
        this.draftEnd = this.getAppliedEnd();
        this.rememberValidDraftDates();
        this.draftStartInvalid = false;
        this.draftEndInvalid = false;
        this.syncDraftDisplays();
    },

    syncDraftDisplays() {
        this.draftStartDisplay = formatDisplayDate(this.draftStart);
        this.draftEndDisplay = formatDisplayDate(this.draftEnd);
    },

    rememberValidDraftDates() {
        if (isValidIsoDate(this.draftStart)) {
            this.lastValidStart = this.draftStart;
        }

        if (isValidIsoDate(this.draftEnd)) {
            this.lastValidEnd = this.draftEnd;
        }
    },

    async selectPreset(preset) {
        if (preset.value === "custom") {
            this.draftPreset = "custom";

            return;
        }

        const freshPreset = this.presets.find((option) => option.value === preset.value) ?? preset;

        this.draftPreset = freshPreset.value;
        this.draftStart = freshPreset.start || "";
        this.draftEnd = freshPreset.end || "";
        this.rememberValidDraftDates();
        this.draftStartInvalid = false;
        this.draftEndInvalid = false;
        this.syncDraftDisplays();
        this.syncCalendar();

        await this.apply();
    },

    presetIsSelected(value) {
        return this.draftPreset === value;
    },

    formatDraftDate(field) {
        if (this.draftPreset === "all-time") {
            return;
        }

        this.draftPreset = "custom";

        if (field === "start") {
            this.draftStartInvalid = false;
        } else {
            this.draftEndInvalid = false;
        }
    },

    commitDraftDate(field) {
        if (this.draftPreset === "all-time") {
            return;
        }

        this.draftPreset = "custom";

        if (field === "start") {
            const iso = parseDisplayDateToIso(this.draftStartDisplay);

            if (!isValidIsoDate(iso)) {
                this.draftStart = this.lastValidStart;
                this.draftStartDisplay = formatDisplayDate(this.lastValidStart);
                this.draftStartInvalid = this.draftStart !== "";
            } else {
                this.draftStart = iso;
                this.draftStartDisplay = formatDisplayDate(iso);
                this.lastValidStart = this.draftStart;
                this.draftStartInvalid = false;
            }
        } else {
            const iso = parseDisplayDateToIso(this.draftEndDisplay);

            if (!isValidIsoDate(iso)) {
                this.draftEnd = this.lastValidEnd;
                this.draftEndDisplay = formatDisplayDate(this.lastValidEnd);
                this.draftEndInvalid = this.draftEnd !== "";
            } else {
                this.draftEnd = iso;
                this.draftEndDisplay = formatDisplayDate(iso);
                this.lastValidEnd = this.draftEnd;
                this.draftEndInvalid = false;
            }
        }

        if (isValidIsoDate(this.draftStart) && isValidIsoDate(this.draftEnd) && this.draftEnd < this.draftStart) {
            this.draftEnd = this.draftStart;
            this.lastValidEnd = this.draftEnd;
            this.draftEndDisplay = formatDisplayDate(this.draftEnd);
        }

        this.syncCalendar();
    },

    commitDraftDates() {
        this.commitDraftDate("start");
        this.commitDraftDate("end");
    },

    draftDatesAreValid() {
        if (this.draftPreset === "all-time") {
            return true;
        }

        if (this.draftPreset === "custom") {
            const appliedPreset = this.getAppliedPreset();
            const appliedStart = this.getAppliedStart();
            const appliedEnd = this.getAppliedEnd();

            if (
                appliedPreset !== "custom"
                && this.draftStart === appliedStart
                && this.draftEnd === appliedEnd
            ) {
                return false;
            }
        }

        return isValidIsoDate(this.draftStart) && isValidIsoDate(this.draftEnd);
    },

    syncCalendar() {
        if (!this.instance) {
            return;
        }

        const dates = [this.draftStart, this.draftEnd].filter((value) => isValidIsoDate(value));
        this.instance.setDate(dates, false);

        const showMonths = this.instance.config.showMonths;
        const focusDate = resolveCalendarFocusDate({
            preset: this.draftPreset,
            start: this.draftStart,
            end: this.draftEnd,
            showMonths,
        });

        this.instance.jumpToDate(focusDate, false);
    },

    async apply() {
        if (this.draftPreset === "custom") {
            this.commitDraftDates();
        }

        if (!this.draftDatesAreValid()) {
            return;
        }

        if (this.isFormMode()) {
            this.appliedPreset = this.draftPreset;
            this.appliedStart = this.draftPreset === "all-time" ? "" : (this.draftStart || "");
            this.appliedEnd = this.draftPreset === "all-time" ? "" : (this.draftEnd || "");
            this.refreshSummaryText();
            this.syncFormInputs();
            this.closePicker();

            if (this.submitOnApply) {
                this.submitFormFilters();
            }

            return;
        }

        // Close before the Livewire round-trip so a slow report rebuild
        // cannot trap the user behind the open picker panel.
        this.closePicker();

        await this.$wire.call(
            applyAction,
            this.draftPreset,
            this.draftStart || null,
            this.draftEnd || null,
        );
    },

    cancel() {
        this.restoreDraft();
        this.closePicker();
    },
});

export const registerDateRangePicker = (Alpine) => {
    Alpine.data("dateRangePicker", (config) => buildDateRangePicker(config));
};
